import React from 'react';
import {
  Binding,
  BindingStatus,
  CreateTaskInput,
  Task,
  TaskDirection,
  TaskSearchType,
  TaskSettlementStatus,
  TaskStatus,
} from '@admiin-com/ds-graphql';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import {
  BillCreateFormData,
  BillCreateFormDataDefaultValues,
} from '../../../providers/TaskCreationProvider/TaskCreationProvider';
import PageSelector from '../../../../components/PageSelector/PageSelector';
import { S3Upload } from '@admiin-com/ds-common';
import { TaxFill } from './TaxFill/TaxFill';
import { TaxReview } from './TaxReview';
import { useTaskCreate } from '../../../providers/TaskCreationProvider/useTaskCreate';
import useSelectedFirm from '../../../../hooks/useSelectedFirm/useSelectedFirm';
import { useTaskCreationContext } from '../../../providers/TaskCreationProvider/useTaskCreationContext';
import { TaxBindingToAssign } from './TaxBindingToAssign/TaxBindingToAssign';
import { Bill } from '../../../../components/PayBill/PayBillCreateForm';
import useSearchTasks from '../../../hooks/useTasksFromUrl/useSearchTasks';
import { useResetTaskContextFromTask } from '../../../providers/TaskCreationProvider/useResetTaskContextFromTask';
import { useCreateBindingTask } from '../../../../hooks/useCreateBindingTask';
import { useSnackbar } from '@admiin-com/ds-web';
import { t } from 'i18next';

type BindingWithExtras = Binding & { sendSMS?: boolean | null };

interface TaxWorkflowProps {
  onComplete: (
    task: CreateTaskInput,
    options?: { binding?: Binding | null }
  ) => void;
  documents: S3Upload[];
  scannedResults: Bill[];
  scanningKeys: Set<string>;
  skippedKeys: Set<string>;
  onSkipScanning: (key: string) => void;
  onAssignEntity?: (
    documentKey: string | null | undefined,
    entity: any
  ) => void;
}

const TaxWorkflow: React.FC<TaxWorkflowProps> = ({
  onComplete,
  documents,
  scannedResults,
  scanningKeys,
  skippedKeys,
  onSkipScanning,
  onAssignEntity,
}) => {
  const { client, task } = useTaskCreationContext();
  const { selectedFirm: firm } = useSelectedFirm();
  const methods = useForm<{
    taxes: BillCreateFormData[];
    emailMessage?: string;
    sendSMS?: boolean;
  }>({
    defaultValues: {
      taxes: documents.map((document) => ({
        ...BillCreateFormDataDefaultValues,
        documents: [document],
        from: firm,
        to: client,
        direction: TaskDirection.RECEIVING,
        settlementStatus: TaskSettlementStatus.PAYABLE,
      })),
      sendSMS: true,
    },
  });
  const { fields, append, update } = useFieldArray({
    control: methods.control,
    name: 'taxes',
  });

  const setTaskData = useResetTaskContextFromTask({
    setValue: methods.setValue,
    reset: methods.reset,
    append,
  });

  const { tasks: bindingTaxes } = useSearchTasks({
    bindingId: task?.bindingId,
    firmId: firm?.id,
    // entityId: client?.id,
    skipCondition: !task?.bindingId || !firm?.id ? true : false,
  });

  const [page, setPage] = React.useState<'toAssign' | 'fill' | 'confirm'>(
    task?.bindingId ? 'fill' : 'toAssign'
  );
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [skipDocumentAnalysisResetMap, setSkipDocumentAnalysisResetMap] =
    React.useState<Record<string, boolean>>({});

  const [sendTemplate, setSendTemplate] = React.useState(false);
  const [templateJson, setTemplateJson] = React.useState<string | null>(null);
  const [templateHtml, setTemplateHtml] = React.useState<string | null>(null);
  const [includeLetterhead, setIncludeLetterhead] = React.useState<
    boolean | null
  >(null);

  React.useEffect(() => {
    if (bindingTaxes.length > 0 && task?.bindingId) {
      methods.reset({ taxes: [] });
      bindingTaxes.forEach((task: Task, index: number) => {
        setTaskData(task, `taxes.${index}`);
      });
      const bindingSendSms = bindingTaxes.some(
        (bindingTask: Task) => bindingTask.sendSMS ?? false
      );
      methods.setValue('sendSMS', bindingSendSms);

      // Initialize sendTemplate from binding tasks when editing
      const bindingSendTemplate = bindingTaxes.some(
        (bindingTask: Task) => bindingTask.sendTemplate ?? false
      );
      setSendTemplate(bindingSendTemplate);

      // Initialize template data from the first task that has it
      const taskWithTemplate = bindingTaxes.find(
        (bindingTask: Task) =>
          bindingTask.templateHtml || bindingTask.templateJson
      );
      if (taskWithTemplate) {
        setTemplateJson(taskWithTemplate.templateJson ?? null);
        setTemplateHtml(taskWithTemplate.templateHtml ?? null);
        setIncludeLetterhead(taskWithTemplate.includeLetterhead ?? null);
      }

      const currentIndex = bindingTaxes.findIndex(
        (bindingTask: Task) => bindingTask.id === task?.id
      );
      setCurrentIndex(currentIndex);
      setPage('fill');
    }
  }, [bindingTaxes, task]);
  // Track which documents have been scanned to prevent re-scanning

  const handleEditTax = (index: number) => {
    setCurrentIndex(index);
    setPage('fill');
  };
  const [sending, setSending] = React.useState(false);
  const [saveAsDraftLoading, setSaveAsDraftLoading] = React.useState(false);
  const [createBindingTask] = useCreateBindingTask();
  const showSnackbar = useSnackbar();

  const handleCompleteReview = async () => {
    const formValues = methods.getValues();
    const bindingSendSMS = formValues.sendSMS ?? null;
    const taxes = formValues.taxes.map((tax) => ({
      ...tax,
      sendSMS: bindingSendSMS ?? false,
    }));
    const emailMessage = formValues.emailMessage;
    const bindId = uuidv4();
    setSending(true);
    try {
      const result = await createBindingTask({
        tasks: taxes,
        emailMessage: emailMessage,
        sendTemplate,
        templateJson: templateJson ?? undefined,
        templateHtml: templateHtml ?? undefined,
        includeLetterhead: includeLetterhead ?? undefined,
        bindingId: bindId,
        sendSMS: bindingSendSMS,
      });
      const created = result?.data?.createBindingTask?.[0];
      if (created) {
        const bindingRecord: BindingWithExtras | null = result?.binding
          ? { ...result.binding, sendSMS: bindingSendSMS ?? null }
          : created.bindingId
          ? {
              __typename: 'Binding' as const,
              id: created.bindingId,
              status: result?.binding?.status ?? BindingStatus.SENT,
              sendTemplate: sendTemplate ?? null,
              emailMessage: emailMessage ?? null,
              sendSMS: bindingSendSMS ?? null,
            }
          : null;
        onComplete(created, {
          binding: bindingRecord,
        });
      }
    } catch (error: any) {
      showSnackbar({
        message: t(error?.message, { ns: 'errors' }),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
    } finally {
      setSending(false);
    }
  };

  const { saveDocument } = useTaskCreate({
    isTaxCreation: true,
    taskType: 'tax',
  });

  const handleSaveAsDraft = async () => {
    const formValues = methods.getValues();
    const bindingSendSMS = formValues.sendSMS ?? null;
    const taxes = formValues.taxes.map((tax) => ({
      ...tax,
      sendSMS: bindingSendSMS ?? false,
    }));
    const emailMessage = formValues.emailMessage;
    const bindId = uuidv4();
    setSaveAsDraftLoading(true);
    try {
      let createdTask: CreateTaskInput | null = null;
      for (const tax of taxes) {
        const task = await saveDocument(
          {
            ...tax,
            bindingId: bindId,
            sendTemplate,
            emailMessage: emailMessage,
            sendSMS: bindingSendSMS ?? false,
          },
          TaskStatus.DRAFT
        );
        createdTask = task;
      }
      if (createdTask) {
        const bindingPayload: BindingWithExtras = {
          __typename: 'Binding',
          id: bindId,
          status: BindingStatus.DRAFT,
          sendTemplate: sendTemplate ?? null,
          emailMessage: emailMessage ?? null,
          sendSMS: bindingSendSMS ?? null,
        };
        onComplete(createdTask, {
          binding: bindingPayload,
        });
      }
    } finally {
      setSaveAsDraftLoading(false);
    }
  };

  const handleSaveForReview = async () => {
    const formValues = methods.getValues();
    const bindingSendSMS = formValues.sendSMS ?? null;
    const taxes = formValues.taxes.map((tax) => ({
      ...tax,
      sendSMS: bindingSendSMS ?? false,
    }));
    const emailMessage = formValues.emailMessage;
    const bindId = uuidv4();
    setSaveAsDraftLoading(true);
    try {
      let createdTask: CreateTaskInput | null = null;
      for (const tax of taxes) {
        const task = await saveDocument(
          {
            ...tax,
            bindingId: bindId,
            sendTemplate,
            emailMessage: emailMessage,
            sendSMS: bindingSendSMS ?? false,
          },
          TaskStatus.REVIEW
        );
        createdTask = task;
      }
      if (createdTask) {
        const bindingPayload: BindingWithExtras = {
          __typename: 'Binding',
          id: bindId,
          status: BindingStatus.REVIEW,
          sendTemplate: sendTemplate ?? null,
          emailMessage: emailMessage ?? null,
          sendSMS: bindingSendSMS ?? null,
        };
        onComplete(createdTask, {
          binding: bindingPayload,
        });
      }
    } finally {
      setSaveAsDraftLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <PageSelector current={page}>
        <PageSelector.Page value="toAssign">
          <TaxBindingToAssign
            onNext={() => setPage('fill')}
            onAssignEntity={onAssignEntity}
          />
        </PageSelector.Page>
        <PageSelector.Page value="fill">
          {fields.map((field, index) => (
            <Controller
              control={methods.control}
              key={field.id}
              name={`taxes.${index}`}
              render={({ field }) =>
                currentIndex === index ? (
                  <TaxFill
                    index={index + 1}
                    totalDocuments={fields.length}
                    onBack={() => {
                      // if (currentIndex > 0) {
                      //   setCurrentIndex(currentIndex - 1);
                      // } else {
                      setPage('toAssign');
                      // }
                    }}
                    value={field.value}
                    onNext={(data) => {
                      field.onChange(data);
                      setSkipDocumentAnalysisResetMap((prev) => ({
                        ...prev,
                        [fields[index].id]: true,
                      }));
                      if (currentIndex < documents.length - 1) {
                        setCurrentIndex(currentIndex + 1);
                      } else {
                        setPage('confirm');
                      }
                    }}
                    nextButtonText={
                      currentIndex === documents.length - 1
                        ? 'Review Documents'
                        : 'Next Document'
                    }
                    scannedTax={
                      scannedResults.find(
                        (result) =>
                          result.documentAnalysis?.fileKey ===
                          field.value.documents?.[0]?.key
                      ) ?? null
                    }
                    scanning={
                      field.value.documents?.[0]?.key
                        ? scanningKeys.has(field.value.documents[0].key)
                        : false
                    }
                    scanningSkipped={
                      field.value.documents?.[0]?.key
                        ? skippedKeys.has(field.value.documents[0].key)
                        : false
                    }
                    onSkipScanning={(key) => onSkipScanning(key)}
                    skipDocumentAnalysisReset={
                      skipDocumentAnalysisResetMap[fields[index].id] ?? false
                    }
                  />
                ) : (
                  <div />
                )
              }
            />
          ))}
        </PageSelector.Page>
        <PageSelector.Page value="confirm">
          <TaxReview
            sendTemplate={sendTemplate}
            onSendTemplateChange={(
              checked,
              templateJson,
              templateHtml,
              includeLetterhead
            ) => {
              setSendTemplate(checked);
              setTemplateJson(templateJson ?? null);
              setTemplateHtml(templateHtml ?? null);
              setIncludeLetterhead(includeLetterhead ?? null);
            }}
            taxes={methods.getValues('taxes')}
            onEdit={handleEditTax}
            onComplete={handleCompleteReview}
            sending={sending}
            onSaveAsDraft={handleSaveAsDraft}
            onSaveForReview={handleSaveForReview}
            saveAsDraftLoading={saveAsDraftLoading}
          />
        </PageSelector.Page>
      </PageSelector>
    </FormProvider>
  );
};

export default TaxWorkflow;
